import { useState } from 'react';
import { DailyReflection, ReflectionFormData } from '@/types/reflection';
import { shouldShowReflectionPrompt } from '@/lib/reflection-utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import {
  NotebookPen,
  ChevronDown,
  ChevronUp,
  Flame,
  Pencil,
  History,
} from 'lucide-react';
import { ReflectionHistory } from './ReflectionHistory';

const MAX_CHARS = 280;

interface ReflectionPromptProps {
  allHabitsComplete: boolean;
  totalHabits: number;
  todayReflection: DailyReflection | null;
  reflectionStreak: number;
  isActivated: boolean;
  isSkipped: boolean;
  loading: boolean;
  saveReflection: (data: ReflectionFormData) => Promise<void>;
  skipToday: () => void;
  reflections: DailyReflection[];
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

type PromptState = 'nudge' | 'form' | 'completed';

export const ReflectionPrompt = ({
  allHabitsComplete,
  totalHabits,
  todayReflection,
  reflectionStreak,
  isActivated,
  isSkipped,
  loading,
  saveReflection,
  skipToday,
  reflections,
  hasMore,
  loadMore,
}: ReflectionPromptProps) => {

  const { toast } = useToast();
  const [state, setState] = useState<PromptState>(() =>
    todayReflection ? 'completed' : 'nudge'
  );
  const [form, setForm] = useState<ReflectionFormData>({
    energized: todayReflection?.energized || '',
    drained: todayReflection?.drained || '',
    changeAction: todayReflection?.changeAction || '',
  });
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Sync state when todayReflection loads after initial render
  if (todayReflection && state === 'nudge') {
    setState('completed');
    setForm({
      energized: todayReflection.energized || '',
      drained: todayReflection.drained || '',
      changeAction: todayReflection.changeAction || '',
    });
  }

  // Gate: don't show if not activated, already skipped, or conditions not met
  if (loading || !isActivated || isSkipped) return null;
  if (!shouldShowReflectionPrompt(allHabitsComplete, totalHabits)) return null;

  const handleSave = async () => {
    // Need at least one field filled
    if (!form.energized.trim() && !form.drained.trim() && !form.changeAction.trim()) {
      toast({
        title: 'Write something',
        description: 'Fill in at least one field before saving.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      await saveReflection(form);
      setState('completed');
      toast({ title: 'Reflection saved' });
    } catch {
      toast({
        title: 'Failed to save',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setForm({
      energized: todayReflection?.energized || '',
      drained: todayReflection?.drained || '',
      changeAction: todayReflection?.changeAction || '',
    });
    setState('form');
  };

  const handleSkip = () => {
    skipToday();
  };

  const updateField = (field: keyof ReflectionFormData, value: string) => {
    if (value.length <= MAX_CHARS) {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  return (
    <div id="reflection-prompt" className="pt-4 space-y-3">
      {/* Nudge state */}
      {state === 'nudge' && (
        <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-center gap-3">
            <NotebookPen className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm font-medium">Quick reflection before you go?</p>
              <p className="text-xs text-muted-foreground">60 seconds — 3 questions</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleSkip}>
              Skip
            </Button>
            <Button size="sm" onClick={() => setState('form')}>
              Reflect
            </Button>
          </div>
        </div>
      )}

      {/* Form state */}
      {state === 'form' && (
        <div className="rounded-lg border border-border/50 bg-muted/30 p-4 space-y-4">
          <div className="flex items-center gap-2">
            <NotebookPen className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold">Daily Reflection</h3>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-emerald-500">
                What energized you today?
              </label>
              <Textarea
                value={form.energized}
                onChange={(e) => updateField('energized', e.target.value)}
                placeholder="A great meeting, flow state on a project..."
                className="mt-1 resize-none"
                rows={2}
              />
              <p className="text-xs text-muted-foreground text-right mt-0.5">
                {form.energized.length}/{MAX_CHARS}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-orange-500">
                What drained you?
              </label>
              <Textarea
                value={form.drained}
                onChange={(e) => updateField('drained', e.target.value)}
                placeholder="Back-to-back calls, unclear expectations..."
                className="mt-1 resize-none"
                rows={2}
              />
              <p className="text-xs text-muted-foreground text-right mt-0.5">
                {form.drained.length}/{MAX_CHARS}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-blue-500">
                What would you change tomorrow?
              </label>
              <Textarea
                value={form.changeAction}
                onChange={(e) => updateField('changeAction', e.target.value)}
                placeholder="Block focus time, say no to one thing..."
                className="mt-1 resize-none"
                rows={2}
              />
              <p className="text-xs text-muted-foreground text-right mt-0.5">
                {form.changeAction.length}/{MAX_CHARS}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (todayReflection) {
                  setState('completed');
                } else {
                  handleSkip();
                }
              }}
            >
              {todayReflection ? 'Cancel' : 'Skip'}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Reflection'}
            </Button>
          </div>
        </div>
      )}

      {/* Completed state */}
      {state === 'completed' && todayReflection && (
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <NotebookPen className="w-4 h-4 text-emerald-500" />
              <p className="text-sm font-medium text-emerald-500">Reflection saved</p>
              {reflectionStreak > 1 && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-500 bg-orange-500/10 px-2 py-0.5 rounded-full">
                  <Flame className="w-3 h-3" />
                  {reflectionStreak}-day streak
                </span>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={handleEdit} className="gap-1">
              <Pencil className="w-3 h-3" />
              Edit
            </Button>
          </div>

          {/* Summary */}
          <div className="space-y-1.5 text-sm">
            {todayReflection.energized && (
              <p>
                <span className="font-medium text-emerald-500">Energized:</span>{' '}
                {todayReflection.energized}
              </p>
            )}
            {todayReflection.drained && (
              <p>
                <span className="font-medium text-orange-500">Drained:</span>{' '}
                {todayReflection.drained}
              </p>
            )}
            {todayReflection.changeAction && (
              <p>
                <span className="font-medium text-blue-500">Change:</span>{' '}
                {todayReflection.changeAction}
              </p>
            )}
          </div>

          {/* History collapsible */}
          <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground gap-1"
              >
                <History className="w-3 h-3" />
                View past reflections
                {historyOpen ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ReflectionHistory
                reflections={reflections.filter(
                  (r) => r.reflectionDate !== todayReflection.reflectionDate
                )}
                hasMore={hasMore}
                onLoadMore={loadMore}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  );
};
